/** @jsxImportSource @emotion/react */
import React, {Fragment, useEffect} from 'react';
import {Controller, get, useWatch} from 'react-hook-form';
import SubHeader from 'app/gakki/components/SubHeader';
import Select from 'components/Select';
import Button from 'components/Button';
import {transferDateObj, formatYYYYMMDD} from 'app/gakki/utils/time';
import {
  FormItem,
  DatePicker,
  ErrorMessage,
  Switch,
} from 'components/form-items';
import {DateTime} from 'luxon';
import Block from './block';
import Show from './show';

const yesorno = [
  {
    label: '是',
    value: 1,
  },
  {
    label: '否',
    value: 0,
  },
];

const IncomeTaxData = (props: any) => {
  const {
    formProps,
    defaultValues,
    options,
    onBoardingDate,
    updateLocale,
    presetEffectiveDate,
    shouldPresetEffectiveDate,
  } = props;
  const {control, formState, errors, trigger, setValue} = formProps;
  const isUnable = !useWatch({
    control,
    name: `tax.enabled`,
    defaultValue: defaultValues.enabled,
  });
  const incomeTaxNumberCode = useWatch({
    control,
    name: `tax.incomeTaxNumberCode`,
    defaultValue: defaultValues.incomeTaxNumberCode,
  });
  const isWithHoldingTypeCodeShow =
    /(IncomeTaxNumber01|IncomeTaxNumber02)/.test(incomeTaxNumberCode);
  const supplementaryPremiumPartTime = useWatch({
    control,
    name: `tax.supplementaryPremiumPartTime`,
    defaultValue: defaultValues.supplementaryPremiumPartTime,
  });
  const isError = (name: string) =>
    !isUnable && formState.isSubmitted && get(errors, name);

  useEffect(() => {
    if (!isUnable && formState.isSubmitted) {
      trigger();
    }
  }, [isUnable, formState.isSubmitted]);
  return (
    <div data-test-id={'income-tax'}>
      <SubHeader>
        <span style={{paddingRight: '15px'}}>所得稅</span>
        <Controller
          render={({onChange, value}) => (
            <div data-test-id={'enabled-tax'} className="flex items-center">
              <Switch
                checked={value}
                onChange={(enabled: Boolean) => {
                  onChange(enabled);
                  if (!enabled) {
                    setValue(`dependentsTax.enabled`, false);
                  }
                  updateLocale();
                }}
              />
            </div>
          )}
          name={`tax.enabled`}
          control={control}
          defaultValue={defaultValues.enabled}
        />
      </SubHeader>
      <Block>
        <Show isShow>
          <FormItem
            label="生效日期"
            itemName="incomeTax_effectiveDate"
            required
            width="160px"
            data-test-id={'effective-date'}
          >
            <Controller
              render={({onChange, value}) => (
                <Fragment>
                  <DatePicker
                    dateFormat="yyyy/MM/dd"
                    minDate={transferDateObj(onBoardingDate)}
                    selected={transferDateObj(value)}
                    disabled={formState.isSubmitting || isUnable}
                    onChange={(date: Date) => {
                      const dateString = formatYYYYMMDD(date);
                      onChange(dateString);
                      updateLocale();
                    }}
                    onBlur={() => {
                      if (value && shouldPresetEffectiveDate === 'true') {
                        presetEffectiveDate(value)
                      }
                    }}
                    onCalendarClose={() => {
                      if (value && shouldPresetEffectiveDate === 'true') {
                        presetEffectiveDate(value)
                      }
                    }}
                    error={isError(`tax.effectiveDate`)}
                  />
                  {isError(`tax.effectiveDate`) && (
                    <ErrorMessage>
                      {get(errors, `tax.effectiveDate`)?.message}
                    </ErrorMessage>
                  )}
                </Fragment>
              )}
              name={`tax.effectiveDate`}
              control={control}
              rules={{
                required: !isUnable && '生效日期為必填',
                validate: {
                  isAfterOnBoardingDate: v => {
                    let onBoardingDateObj = transferDateObj(onBoardingDate);
                    let effectiveDateObj = transferDateObj(v);
                    if (!isUnable && onBoardingDateObj && effectiveDateObj) {
                      return (
                        DateTime.fromJSDate(onBoardingDateObj) <=
                          DateTime.fromJSDate(effectiveDateObj) ||
                        `不可早於到職日${onBoardingDate}`
                      );
                    }
                    return true;
                  },
                },
              }}
              defaultValue={defaultValues.effectiveDate}
            />
          </FormItem>
          <FormItem
            label="申報單位"
            itemName="incomeTax_incomeTaxReturnUnitId"
            required
            width="200px"
            data-test-id={'income-tax-return-unit-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={
                    options.incomeTaxRegistrationUnits.find(
                      (o: any) => o.incomeTaxReturnUnitId === value,
                    ) || null
                  }
                  options={options.incomeTaxRegistrationUnits}
                  onChange={(o: any) => {
                    onChange(o.incomeTaxReturnUnitId);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) =>
                    `${o.unifiedBusinessNo} ${o.declarationName}`
                  }
                  getOptionValue={(o: any) => o.incomeTaxReturnUnitId}
                  error={isError(`tax.incomeTaxReturnUnitId`)}
                />
              )}
              name={`tax.incomeTaxReturnUnitId`}
              control={control}
              rules={{required: !isUnable && '申報單位為必填'}}
              defaultValue={defaultValues.incomeTaxReturnUnitId}
            />
          </FormItem>
          <FormItem
            label="證號別"
            itemName="incomeTax_incomeTaxNumberCode"
            required
            width="200px"
            data-test-id={'income-tax-number-code'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={
                    options.incomeTaxNumbers.find(
                      (o: any) => o.value === value,
                    ) || null
                  }
                  options={options.incomeTaxNumbers}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    if (
                      !/(IncomeTaxNumber01|IncomeTaxNumber02)/.test(o.value)
                    ) {
                      setValue(`tax.withHoldingTypeCode`, null);
                    }
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) => o.item}
                  error={isError(`tax.incomeTaxNumberCode`)}
                />
              )}
              name={`tax.incomeTaxNumberCode`}
              control={control}
              rules={{required: !isUnable && '證號別為必填'}}
              defaultValue={defaultValues.incomeTaxNumberCode}
            />
          </FormItem>
          <FormItem
            label="扣繳方式"
            itemName="incomeTax_withHoldingTypeCode"
            required
            width="180px"
            hidden={!isWithHoldingTypeCodeShow}
            data-test-id={'with-holding-type-code'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={
                    options.incomeTaxWithholdingTypes.find(
                      (o: any) => o.value === value,
                    ) || null
                  }
                  options={options.incomeTaxWithholdingTypes}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) => o.item}
                  error={isError(`tax.withHoldingTypeCode`)}
                />
              )}
              name={`tax.withHoldingTypeCode`}
              control={control}
              rules={{
                required:
                  isWithHoldingTypeCodeShow && !isUnable && '扣繳方式為必填',
              }}
              defaultValue={defaultValues.withHoldingTypeCode}
            />
          </FormItem>
          <FormItem
            label="計算兼職薪資補充保費"
            itemName="incomeTax_supplementaryPremiumPartTime"
            required
            width="145px"
            data-test-id={'supplementary-premium-part-time'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={yesorno.find(o => o.value === value)}
                  options={yesorno}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  error={isError(`tax.supplementaryPremiumPartTime`)}
                />
              )}
              name={`tax.supplementaryPremiumPartTime`}
              control={control}
              rules={{
                validate: v =>
                  isUnable ||
                  typeof v === 'number' ||
                  '計算兼職薪資補充保費為必填',
              }}
              defaultValue={defaultValues.supplementaryPremiumPartTime}
            />
          </FormItem>
          <FormItem
            label="兼職薪資補充保費單位"
            itemName="incomeTax_supplementaryPremiumPartTimeUnitId"
            hidden={!Boolean(supplementaryPremiumPartTime)}
            required
            width="200px"
            data-test-id={'supplementary-premium-part-time-unit-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={
                    options.insuranceUnits.find(
                      (o: any) => o.insuranceApplicantUnitId === value,
                    ) || null
                  }
                  options={options.insuranceUnits}
                  onChange={(o: any) => {
                    onChange(o.insuranceApplicantUnitId);
                    updateLocale();
                  }}
                  isDisabled={
                    !Boolean(supplementaryPremiumPartTime) ||
                    formState.isSubmitting ||
                    isUnable
                  }
                  getOptionLabel={(o: any) =>
                    `${o.insuranceNo} ${o.insuranceApplicantUnitName}`
                  }
                  getOptionValue={(o: any) => o.insuranceApplicantUnitId}
                  error={
                    Boolean(supplementaryPremiumPartTime) &&
                    isError(`tax.supplementaryPremiumPartTimeUnitId`)
                  }
                />
              )}
              name={`tax.supplementaryPremiumPartTimeUnitId`}
              control={control}
              rules={{
                required:
                  Boolean(supplementaryPremiumPartTime) &&
                  !isUnable &&
                  '兼職薪資補充保費單位為必填',
              }}
              defaultValue={defaultValues.supplementaryPremiumPartTimeUnitId}
            />
          </FormItem>
          <FormItem
            label="補充保費弱勢族群身分"
            itemName="incomeTax_disadvantagedGroup"
            required
            width="145px"
            data-test-id={'disadvantaged-group'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={yesorno.find(o => o.value === value)}
                  options={yesorno}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  error={isError(`tax.disadvantagedGroup`)}
                />
              )}
              name={`tax.disadvantagedGroup`}
              control={control}
              rules={{
                validate: v =>
                  isUnable ||
                  typeof v === 'number' ||
                  '補充保費弱勢族群身分為必填',
              }}
              defaultValue={defaultValues.disadvantagedGroup}
            />
          </FormItem>
        </Show>
      </Block>
    </div>
  );
};

export default IncomeTaxData;
